import React, { useEffect, useMemo } from 'react';
import { find, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { Box, Button, CardContent, FilePdfIcon, Grid, Link, Paper, TextField, Typography } from '@availity/element';

import { AvailityHeaderLogo, Footer, MessageDisplay } from '@/components';
import { useAppContext } from '@/context';
import { getHiddenValueCallbackById } from '@/global';
import { useFRCallback } from '@/hooks';
import { TwoFAIcon01 } from '@/static';

const faqLink = 'https://www.availity.com/documents/learning/QRG_AP_2step_Providers.pdf';

const TwoFAHotpInputToken = ({ ...props }) => {
  const { stepToRender, setCurrentStep, errorCode } = props;
  const {
    callbacks = [],
    confirmationCallbackIndex,
    confirmationCallbackOptions,
    textOutputCallback,
  } = useFRCallback(stepToRender);

  const { setLoading, addMessage, setMessages } = useAppContext();

  // build form handler
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      code: '',
    },
  });

  // set field error if an errorCode comes back
  useEffect(() => {
    if (errorCode === '106') {
      setError('code', { type: 'custom', message: 'Enter a valid code.' });
    }
  }, [errorCode]);

  // get the device number
  const deviceNumber = find(
    textOutputCallback?.output,
    (outputObj) => outputObj?.name?.toLowerCase() === 'message'
  )?.value;

  // get the delivery method (sms or tts)
  const deliveryMethod = useMemo(() => {
    let result;
    if (callbacks) {
      const deliverymethodCallback = getHiddenValueCallbackById(callbacks, 'otpmethod');
      const deliveryMethodOutput = deliverymethodCallback?.output;
      result =
        deliveryMethodOutput &&
        find(deliveryMethodOutput, (outputObj) => outputObj?.name?.toLowerCase() === 'value')?.value;
    }

    return result?.toLowerCase();
  }, [callbacks]);

  const hasBackNavigation = useMemo(() => {
    let output = false;

    if (confirmationCallbackOptions) {
      for (const option of confirmationCallbackOptions) {
        const formattedOption = option?.toLowerCase();
        if (formattedOption === 'back') {
          output = true;
        }
      }
    }

    return output;
  }, [confirmationCallbackOptions]);

  const onSubmit = (values) => {
    setLoading(true);

    const { code = '' } = values;
    const nextStep = { ...stepToRender };
    const newCallbacks = [...callbacks];

    // get index of the selected navigation button
    // TODO: verify resend code option verbiage
    const navigationButtonIndex = findIndex(
      confirmationCallbackOptions,
      (option) => option?.toLowerCase() === 'verify'
    );

    // set the confirmation callback input based off the index of the selected option in the callback
    newCallbacks[confirmationCallbackIndex].input[0] = {
      ...newCallbacks[confirmationCallbackIndex].input[0],
      value: navigationButtonIndex,
    };

    // get index of name callback
    const nameCallbackObjIndex = findIndex(
      callbacks,
      (callbackObj) => callbackObj?.type?.toLowerCase() === 'namecallback'
    );

    // set the name callback input
    newCallbacks[nameCallbackObjIndex].input[0] = { ...newCallbacks[nameCallbackObjIndex].input[0], value: code };

    // set the newCallbacks in the newStep
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // update the currentStep to trigger navigation
    setCurrentStep(nextStep);

    setLoading(false);
  };

  const prompt = useMemo(() => {
    let output;
    if (deliveryMethod === 'voice') {
      output = (
        <Box mt={2} mb={3}>
          <Typography variant="subtitle1" mb={2}>
            We&apos;re calling you with a code to{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {deviceNumber}
            </Box>
            .
          </Typography>
          <Typography variant="subtitle1">
            You have{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              6 minutes
            </Box>{' '}
            to enter your code.
          </Typography>
        </Box>
      );
    }

    if (deliveryMethod === 'sms') {
      output = (
        <Box mt={2} mb={3}>
          <Typography variant="subtitle1" mb={2}>
            We&apos;ve texted a code to{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              {deviceNumber}
            </Box>
            .
          </Typography>
          <Typography variant="subtitle1">
            You have{' '}
            <Box component="span" sx={{ fontWeight: 'bold' }}>
              10 minutes
            </Box>{' '}
            to enter your code.
          </Typography>
        </Box>
      );
    }

    return output;
  }, [deliveryMethod, deviceNumber]);

  const handleNavigation = (selected) => {
    const nextStep = { ...stepToRender };
    const newCallbacks = [...callbacks];

    // get index of the selected navigation button
    let navigationButtonIndex;
    if (selected === 'back') {
      navigationButtonIndex = findIndex(confirmationCallbackOptions, (option) => option?.toLowerCase() === 'back');
    } else if (selected === 'resend') {
      navigationButtonIndex = findIndex(confirmationCallbackOptions, (option) => option?.toLowerCase() === 'resend');

      addMessage({
        type: 'success',
        text: "We've sent a new code to your device!",
        icon: 'icon-attention-circle',
        timeout: 5 * 1000, // 5 seconds
        dismissible: true,
      });
    }

    // set the confirmation callback input based off the index of the selected option in the callback
    newCallbacks[confirmationCallbackIndex].input[0] = {
      ...newCallbacks[confirmationCallbackIndex].input[0],
      value: navigationButtonIndex,
    };

    // set the newCallbacks in the newStep
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // update the currentStep to trigger navigation
    setCurrentStep({ ...nextStep });
  };

  return (
    <Grid
      id="2fa-hotp-input-token-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-start-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '750px', lg: '1000px' } }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                <Grid display="flex" flexDirection="row" flexWrap="wrap">
                  <Grid size={{ sm: 12, md: 4 }} sx={{ marginBottom: { xs: 2, md: 0 }, paddingRight: 2 }}>
                    <Box textAlign="center" mb={2}>
                      <img
                        src={TwoFAIcon01}
                        alt="2FA Icon"
                        style={{
                          maxWidth: '45%',
                        }}
                      />
                    </Box>
                    <Typography variant="h5" component="h3" mb={2}>
                      What is 2-step authentication?
                    </Typography>
                    <Typography variant="subtitle1">
                      2-step authentication protects your account by asking you for two pieces of information: your
                      username & password combination plus a six digit code that will be sent to you.
                    </Typography>
                    <Box mt={3}>
                      <Link
                        href={faqLink}
                        target="_blank"
                        loadApp={false}
                        rel="noopener noreferrer"
                        aria-label="Get answers to common questions (opens PDF in a new window)"
                      >
                        Answers to common questions <FilePdfIcon fontSize="small" />
                      </Link>
                    </Box>
                  </Grid>
                  <Grid
                    display="flex"
                    flexDirection="column"
                    size={{ md: 8 }}
                    sx={{ borderLeft: { sm: 0, md: 1 }, borderColor: { md: '#D7DAE0' } }}
                  >
                    <Box pl={2}>
                      <Typography variant="h1" mb={1}>
                        2-Step Authentication
                      </Typography>
                      <Typography variant="h4" component="h2" mb={2}>
                        Verify Your Account
                      </Typography>
                    </Box>
                    <Grid display="flex" flexDirection="column" flexGrow={1} pl={2}>
                      {prompt}
                      <TextField
                        id="code"
                        label="Code"
                        margin="normal"
                        fullWidth={false}
                        autoFocus
                        onFocus={() => setMessages([])}
                        {...register('code', {
                          required: 'Enter a valid code.',
                        })}
                        error={!!errors.code}
                        helperText={errors.code?.message}
                      />
                      <MessageDisplay />
                      <Box mb={5}>
                        <Button color="tertiary" size="small" onClick={() => handleNavigation('resend')}>
                          Resend Code
                        </Button>
                      </Box>
                      <Box textAlign="right" mt="auto">
                        {hasBackNavigation && (
                          <Button color="secondary" onClick={() => handleNavigation('back')}>
                            Back
                          </Button>
                        )}
                        <Button color="primary" sx={{ marginLeft: 1 }} type="submit">
                          Continue
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </form>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

TwoFAHotpInputToken.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  errorCode: PropTypes.string,
};

export default TwoFAHotpInputToken;
